import { useCallback, useEffect, useState } from 'react';
import { Button, Chip, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CommonListTable, { CommonListColumn } from '../Common/CommonListTable';
import BookByDishService from '../../services/BookByDishService';
import { formatDate } from '../../utils/DateTimeUtils';

const bookByDishService = new BookByDishService();

interface InputProps {
    customerId: number;
}

const CustomerCartTab = ({ customerId }: InputProps) => {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const fetchData = useCallback(() => {
        if (!customerId) return;
        setLoading(true);
        bookByDishService
            .fetchAllCarts(rowsPerPage, page * rowsPerPage, { userId: customerId })
            .then((res) => {
                setData(res.data);
                setTotal(res.total);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [customerId, page, rowsPerPage]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const columns: CommonListColumn[] = [
        { label: 'Cart ID', field: 'id' },
        { label: 'Date', render: (row) => (row.date ? formatDate(row.date) : '-') },
        { label: 'Time', render: (row) => row.time ?? '-' },
        { label: 'Items', render: (row) => row.items?.length ?? 0 },
        { label: 'Dishes Price', render: (row) => `₹${row.dishesPrice ?? 0}` },
        { label: 'Visit Charges', render: (row) => `₹${row.visitCharges ?? 0}` },
        { label: 'Total Price', render: (row) => `₹${row.totalPrice ?? 0}` },
        {
            label: 'Status',
            render: (row) =>
                row.checkoutPartyId ? (
                    <Chip label="Checked Out" color="success" size="small" />
                ) : (
                    <Chip label="Active" color="primary" size="small" />
                ),
        },
        {
            label: 'Actions',
            stopRowClick: true,
            render: (row) =>
                row.checkoutPartyId ? (
                    <Link href={`/#/ChefForParty/${row.checkoutPartyId}/show`}>
                        <Button variant="contained" size="small" color="primary">
                            Party #{row.checkoutPartyId}
                        </Button>
                    </Link>
                ) : (
                    <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={() =>
                            navigate(`/ChefForParty/create?type=BOOK_BY_DISH&customerId=${customerId}`)
                        }
                    >
                        Open Cart
                    </Button>
                ),
        },
    ];

    return (
        <CommonListTable
            columns={columns}
            data={data}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            total={total}
            onPageChange={setPage}
            onRowsPerPageChange={(rows) => {
                setRowsPerPage(rows);
                setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            emptyText="No carts found for this customer"
        />
    );
};

export default CustomerCartTab;
